import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, ArrowRight, AlertCircle } from 'lucide-react';
import { API } from '../../App';
import QMGSymbols from '../../components/QMGSymbols';

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 8;

const LockerSubscribeSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | success | failed
  const [message, setMessage] = useState('Confirming your subscription…');
  const attempts = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      setMessage('Missing session ID.');
      return;
    }

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await API.get(`/locker/subscription/status/${sessionId}`);
        if (data.status === 'success') {
          setStatus('success');
          setMessage(data.message || 'Your subscription is active!');
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus('failed');
          setMessage('We could not confirm your subscription in time. If you were charged, please refresh in a minute or contact support.');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus('failed');
          setMessage(err?.response?.data?.detail || 'Could not confirm subscription.');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6" data-testid="locker-subscribe-success">
      <div className="bg-white border border-[#E7E5E4] shadow-lg max-w-lg w-full p-8 md:p-10 text-center">
        {status === 'checking' && (
          <>
            <div className="w-12 h-12 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin mx-auto mb-5"></div>
            <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-2">Just a moment</h1>
            <p className="text-[#57534E]">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#2C5530] text-white flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7" />
            </div>
            <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Welcome to the Locker</p>
            <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-3">You're in.</h1>
            <p className="text-[#57534E] mb-6">{message}</p>
            <div className="mb-6 flex justify-center">
              <QMGSymbols size="sm" tone="dark" />
            </div>
            <Link
              to="/locker"
              className="inline-flex items-center gap-2 bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors"
              data-testid="success-go-to-locker"
            >
              Enter Player's Locker <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {status === 'failed' && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#FFF4ED] text-[#BC562B] flex items-center justify-center mx-auto mb-5">
              <AlertCircle className="w-7 h-7" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-3">We couldn't confirm payment</h1>
            <p className="text-[#57534E] mb-6">{message}</p>
            <Link
              to="/locker/paywall"
              className="inline-flex items-center gap-2 border border-[#1A2E1D] px-6 py-3 font-semibold text-[#1A1A1A] hover:bg-[#FAFAF9] transition-colors"
              data-testid="success-back-to-paywall"
            >
              Back to Locker plans <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default LockerSubscribeSuccessPage;
